/**
 * The admin scope — which slice of VOTD an editor view shows (spec: docs/product/admin/admin-scope-and-gaps-spec.md,
 * approved 2026-10-27; handoff admin-scope-picker-handoff-2026-10-27.md).
 *
 *   Federal                        US Congress votes only
 *   State ▸ CA                     every vote at that state's level (by jurisdiction, not by where the vote came from:
 *                                  founder 2026-11-01, "the queue is a juris queue")
 *   County ▸ CA ▸ <lit county>     everything from the county down in one list (county board, cities, schools, special
 *                                  districts, shared bodies with an area there), optionally narrowed to one type or one city
 *
 * No "everything" view and no county badge (founder 2026-10-27). One scope for every editor page; tools pages ignore it.
 * The scope lives in the URL (?scope=county:ca:sacramento&in=cities) so a link opens the same view, and per browser
 * (localStorage, best effort) so the editor comes back to the last one. Default: the first served county.
 *
 * Which bodies are in a scope, which counties are lit and where the gaps are come from the database — the one rule,
 * never re-derived here: api_admin.scope_counties / scope_body_ids / scope_gaps (dev-stage first; Prod with the
 * founder's go). A database without those doors (Prod before the migration) has no scope: the picker hides and every
 * page shows what it showed before.
 *
 * Bucket of a row, as the scheduler and the gap view count it: media-origin → media; else by tier — COUNTY → county
 * board, MUNICIPAL → that city, SCHOOL → schools, SPECIAL_DISTRICT → special districts; FEDERAL / STATE → that level.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminDb } from './supabase';
import type { Env } from './env';

export type Scope =
  | { level: 'federal' }
  | { level: 'state'; state: string }
  | { level: 'county'; state: string; county: string };   // county = county_area_id

/** Narrowing inside a County scope: a type, or one city (by body id). */
export type Narrow = null | 'county' | 'cities' | 'schools' | 'specials' | 'media' | `city:${string}`;

export type ScopeCounty = { state: string; county_area_id: string; county: string; served: boolean; lit: boolean };
export type ScopeGap = {
  bucket: string; label: string; city_body_id: string | null; lit: boolean; gap: boolean; days_left: number;
  runs_dry_on: string | null; in_pool: number; drafts_in_review: number; approved_waiting: number;
  scheduled_ahead: number; nearest_stage: string;
};
/** What a page hands in to ask "is this row in scope?" / "how short is its bucket?". */
export type ScopeRow = { body_id?: string | null; tier?: string | null; origin_pillar?: string | null; pipeline_origin?: string | null };

/** Editor pages follow the scope; everything else ignores it (spec §3). */
export const SCOPED_PAGES = new Set([
  'suggested-votes', 'sourced-votes', 'pipeline-triage', 'upcoming-votes', 'open-votes', 'closed-votes', 'sample-votes',
  'schedule', 'curation-reporting', 'promoter-runs',
]);

const STATE_NAME: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
export const stateName = (st: string) => STATE_NAME[st] ?? st;

const doorMissing = (e: { code?: string; message: string }) => e.code === 'PGRST202' || /could not find the function/i.test(e.message);
export const countySlug = (name: string) => name.toLowerCase().replace(/\s+county$/, '').replace(/[^a-z0-9]+/g, '-');

export function bucketOf(row: ScopeRow): string | null {
  if (row.origin_pillar === 'media' || row.pipeline_origin === 'media') return 'media';
  switch ((row.tier ?? '').toUpperCase()) {
    case 'COUNTY': return 'county';
    case 'MUNICIPAL': return row.body_id ? `city:${row.body_id}` : 'cities';
    case 'SCHOOL': return 'schools';
    case 'SPECIAL_DISTRICT': return 'specials';
    case 'FEDERAL': return 'federal';
    case 'STATE': return 'state';
    default: return null;
  }
}

function encode(s: Scope, counties: ScopeCounty[]): string {
  if (s.level === 'federal') return 'federal';
  if (s.level === 'state') return `state:${s.state.toLowerCase()}`;
  const c = counties.find(x => x.county_area_id === s.county);
  // County names repeat across states (30-odd Washington Counties), so the link carries the state: county:ca:sacramento.
  return c ? `county:${c.state.toLowerCase()}:${countySlug(c.county)}` : `county:${s.county}`;
}
function decode(v: string | null, counties: ScopeCounty[]): Scope | null {
  if (!v) return null;
  if (v === 'federal') return { level: 'federal' };
  const [lvl, arg, arg2] = v.split(':');
  if (lvl === 'state' && arg) return { level: 'state', state: arg.toUpperCase() };
  if (lvl === 'county' && arg) {
    // county:<state>:<slug>; the older county:<slug> and county:<id> still open.
    const st = arg2 ? arg.toUpperCase() : null;
    const key = arg2 ?? arg;
    const c = counties.find(x => x.lit && (!st || x.state === st) && (countySlug(x.county) === key || x.county_area_id === key));
    return c ? { level: 'county', state: c.state, county: c.county_area_id } : null;
  }
  return null;
}
function readStored(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
function writeStored(key: string, v: string) { try { localStorage.setItem(key, v); } catch { /* best effort */ } }

type Ctx = {
  /** null while checking; false = this database has no scope doors (Prod before the migration) — pages show everything. */
  available: boolean | null;
  scope: Scope | null;
  setScope: (s: Scope) => void;
  narrow: Narrow;
  setNarrow: (n: Narrow) => void;
  counties: ScopeCounty[];
  gaps: ScopeGap[];
  /** True when the page should filter (available, scoped page, body list loaded). */
  ready: boolean;
  inScope: (row: ScopeRow) => boolean;
  /** In the scope, ignoring any narrowing to one type or city — what a page offers as its narrowing choices
   *  (founder 2026-11-01: "should only be able to filter options within the view"). */
  inScopeWhole: (row: ScopeRow) => boolean;
  /** Days of supply ahead in the row's bucket (smaller = needs it more); Infinity when unknown or not lit. */
  daysLeft: (row: ScopeRow) => number;
  label: string;
  /** The scope as the database's scope functions take it (p_level, p_state, p_county), or null. */
  args: { p_level: string; p_state: string | null; p_county: string | null } | null;
  /** Switch to a scope that shows this row (for links into a card, e.g. ?open=<id>): its level for federal / state,
   *  else the first lit county whose bodies include the row's body. */
  fitRow: (row: ScopeRow) => Promise<void>;
};

const ScopeContext = createContext<Ctx | null>(null);

export function ScopeProvider({ env, children }: { env: Env; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const segment = location.pathname.split('/')[2] ?? '';
  const scopedPage = SCOPED_PAGES.has(segment);
  const storeKey = `admin.scope.${env}`;
  const params = new URLSearchParams(location.search);

  const countiesQ = useQuery({
    queryKey: ['scope_counties', env],
    queryFn: async (): Promise<ScopeCounty[] | 'missing'> => {
      const { data, error } = await adminDb(env).rpc('scope_counties');
      if (error && doorMissing(error)) return 'missing';
      if (error) throw new Error(error.message);
      return (data ?? []) as ScopeCounty[];
    },
    staleTime: 5 * 60_000,
  });
  const available = countiesQ.data === undefined ? (countiesQ.isError ? false : null) : countiesQ.data !== 'missing';
  const counties = useMemo(() => (Array.isArray(countiesQ.data) ? countiesQ.data : []), [countiesQ.data]);

  const [scope, setScopeState] = useState<Scope | null>(null);
  const [narrow, setNarrowState] = useState<Narrow>(null);

  // Settle the scope once the counties are known: URL first, then this browser's last, then the first served county.
  useEffect(() => {
    if (!available || counties.length === 0) return;
    const fromUrl = decode(params.get('scope'), counties);
    const next = fromUrl ?? (scope ? null : decode(readStored(storeKey), counties)) ?? (scope ? null : (() => {
      const c = counties.find(x => x.served) ?? counties.find(x => x.lit);
      return c ? { level: 'county', state: c.state, county: c.county_area_id } as Scope : { level: 'state', state: 'CA' } as Scope;
    })());
    if (next && (!scope || encode(next, counties) !== encode(scope, counties))) {
      setScopeState(next);
      if (fromUrl) setNarrowState((params.get('in') as Narrow) ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, counties, location.search]);

  // Keep the URL of a scoped page saying which scope it shows, so the link reopens the same view.
  useEffect(() => {
    if (!scope || !scopedPage) return;
    const want = encode(scope, counties);
    const p = new URLSearchParams(location.search);
    if (p.get('scope') === want && (p.get('in') ?? null) === (narrow ?? null)) return;
    p.set('scope', want);
    if (narrow) p.set('in', narrow); else p.delete('in');
    navigate({ pathname: location.pathname, search: `?${p.toString()}` }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, narrow, scopedPage, location.pathname]);

  const setScope = useCallback((s: Scope) => {
    setScopeState(prev => {
      if (!prev || prev.level !== s.level || (prev.level === 'county' && s.level === 'county' && prev.county !== s.county)) setNarrowState(null);
      return s;
    });
    writeStored(storeKey, encode(s, counties));
  }, [counties, storeKey]);
  const setNarrow = useCallback((n: Narrow) => setNarrowState(n), []);

  const args = scope
    ? { p_level: scope.level, p_state: scope.level === 'federal' ? null : scope.state, p_county: scope.level === 'county' ? scope.county : null }
    : null;
  const bodiesQ = useQuery({
    queryKey: ['scope_body_ids', env, args],
    enabled: !!args && available === true,
    queryFn: async () => {
      const { data, error } = await adminDb(env).rpc('scope_body_ids', args!);
      if (error) throw new Error(`Could not load the bodies in this scope: ${error.message}`);
      return new Set(((data ?? []) as Array<{ body_id: string }>).map(r => r.body_id));
    },
    staleTime: 5 * 60_000,
  });
  const gapsQ = useQuery({
    queryKey: ['scope_gaps', env, args],
    enabled: !!args && available === true,
    queryFn: async () => {
      const { data, error } = await adminDb(env).rpc('scope_gaps', args!);
      if (error) throw new Error(`Could not load the gaps for this scope: ${error.message}`);
      return (data ?? []) as ScopeGap[];
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  const gaps = useMemo(() => gapsQ.data ?? [], [gapsQ.data]);
  const bodyIds = bodiesQ.data ?? null;
  const ready = available === true && scopedPage && !!scope && !!bodyIds;

  const daysByBucket = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of gaps) {
      const key = g.bucket === 'city' && g.city_body_id ? `city:${g.city_body_id}` : g.bucket;
      // County cover is never short in the sense that matters: with no county vote ready, local days take a state or
      // federal cover vote (founder 2026-10-27). So county drafts do not jump the needs-first order.
      m.set(key, g.lit && g.bucket !== 'county' ? g.days_left : Number.POSITIVE_INFINITY);
    }
    return m;
  }, [gaps]);

  const inScopeWhole = useCallback((row: ScopeRow) => {
    if (!ready || !scope || !bodyIds) return true;
    const tier = (row.tier ?? '').toUpperCase();
    // A row with no body yet (a handful on dev-stage's pipeline) is never hidden: it shows in its level's scope,
    // or in every county scope when it is local.
    if (!row.body_id) {
      if (scope.level === 'federal') return tier === 'FEDERAL';
      if (scope.level === 'state') return tier === 'STATE';
      if (tier === 'FEDERAL' || tier === 'STATE') return false;
      return true;
    }
    return bodyIds.has(row.body_id);
  }, [ready, scope, bodyIds]);

  const inScope = useCallback((row: ScopeRow) => {
    if (!inScopeWhole(row)) return false;
    if (!ready || !scope || scope.level !== 'county' || !narrow) return true;
    const b = bucketOf(row);
    if (narrow === 'cities') return !!b && (b === 'cities' || b.startsWith('city:'));
    return b === narrow;
  }, [inScopeWhole, ready, scope, narrow]);

  const daysLeft = useCallback((row: ScopeRow) => {
    const b = bucketOf(row);
    if (!b) return Number.POSITIVE_INFINITY;
    return daysByBucket.get(b) ?? Number.POSITIVE_INFINITY;
  }, [daysByBucket]);

  const queryClient = useQueryClient();
  const fitRow = useCallback(async (row: ScopeRow) => {
    if (available !== true) return;
    const tier = (row.tier ?? '').toUpperCase();
    setNarrowState(null);
    if (tier === 'FEDERAL') { setScope({ level: 'federal' }); return; }
    if (tier === 'STATE') { setScope({ level: 'state', state: scope && scope.level !== 'federal' ? scope.state : 'CA' }); return; }
    if (!row.body_id) return;
    for (const c of counties.filter(x => x.lit)) {
      const a = { p_level: 'county', p_state: c.state, p_county: c.county_area_id };
      const ids = await queryClient.fetchQuery({
        queryKey: ['scope_body_ids', env, a], staleTime: 5 * 60_000,
        queryFn: async () => {
          const { data, error } = await adminDb(env).rpc('scope_body_ids', a);
          if (error) throw new Error(error.message);
          return new Set(((data ?? []) as Array<{ body_id: string }>).map(r => r.body_id));
        },
      }).catch(() => null);
      if (ids?.has(row.body_id)) { setScope({ level: 'county', state: c.state, county: c.county_area_id }); return; }
    }
  }, [available, counties, env, queryClient, scope, setScope]);

  const label = !scope ? '' : scope.level === 'federal' ? 'US Congress'
    : scope.level === 'state' ? `${stateName(scope.state)} (state level)`
    : (counties.find(c => c.county_area_id === scope.county)?.county ?? 'County');

  const value: Ctx = { available, scope, setScope, narrow, setNarrow, counties, gaps, ready, inScope, inScopeWhole, daysLeft, label, fitRow, args };
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope(): Ctx {
  const v = useContext(ScopeContext);
  if (!v) throw new Error('useScope outside ScopeProvider');
  return v;
}
